import { FC, useMemo, useRef } from "react";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiTypography,
} from "../../../../components";
import { useGridContainer } from "../../../../hooks/useGridContainer";

import { MuiDoubleLabelWithChip } from "../../../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { CreateUserRoleDetailWrapper } from "../../../createUser/components/createUserRoleDetail/CreateUserRoleDetail.style";
import {
  CandidatePortalRole,
  ViewUserDataType,
} from "../../../createUser/modelsAndSchemas/models";
import { createUserSiteCoreKeys } from "../../../createUser/siteCore/SiteCoreKeys";

import { getFullName, getValueOrBlank } from "../../../../helpers/utils";

interface ViewUserRoleDetailProps {
  userData: ViewUserDataType;
}

export const ViewUserRoleDetail: FC<ViewUserRoleDetailProps> = ({
  userData,
}) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);
  const { useFetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const gridRef = useRef<HTMLDivElement>(null);
  const countryRef = useRef<HTMLDivElement>(null);
  const getUserLabel = useFetchLabel(createUserSiteCoreKeys.userRoleKey);
  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  const userSelectedData = useMemo(() => {
    const roles = userData?.adminUserRoles?.candidatePortalRole?.map(
      (role: CandidatePortalRole) => ({
        label: getValueOrBlank(role.role),
        value: getValueOrBlank(role.roleId),
      }),
    );
    const officeList = userData?.adminUserRoles?.serviceOffices || [];
    const countries = Array.from(
      new Map(
        officeList?.map((office) => {
          const value = getValueOrBlank(office.countryCodeName);
          return [value, { label: value, value }];
        }),
      ).values(),
    );

    const serviceOffices = Array.from(
      new Map(
        officeList
          .filter((office) => !!office.officeName)
          .map((office) => {
            const value = getValueOrBlank(office.officeName);
            return [value, { label: value, value }];
          }),
      ).values(),
    );

    return { roles, countries, serviceOffices };
  }, [userData]);

  return (
    <CreateUserRoleDetailWrapper>
      <MuiGrid container className="user-role-email-grid">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getUserLabel(createUserSiteCoreKeys.userEmail)}
            replaceEmptyWithDash
            value={userData?.emailAddress}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getUserLabel(createUserSiteCoreKeys.userName)}
            replaceEmptyWithDash
            value={getFullName(userData?.firstName, userData?.lastName, true)}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getUserLabel(
              createUserSiteCoreKeys.preferredNameRoleDetails,
            )}
            replaceEmptyWithDash
            value={getValueOrBlank(userData?.username)}
            isValueWithEllipsis
          />
        </MuiGrid>
      </MuiGrid>
      <MuiBox>
        <AccordionHeader
          className="accordion-header"
          title={renderHeader(
            getUserLabel(createUserSiteCoreKeys.userRolesSectionTitle),
          )}
          isExpanded={true}
          showExpandCollapseIcon={false}
        >
          <MuiBox className="accordion-container">
            <MuiTypography
              variant="h2SemiBold"
              className="role-header-text"
              component="div"
            >
              {getUserLabel(
                createUserSiteCoreKeys.cirrusP3AndCandidatePortalSubTitle,
              )}
            </MuiTypography>
            <MuiGrid container>
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabelWithChip
                  label={getUserLabel(
                    createUserSiteCoreKeys.cirrusP3AndCandidatePortalRole,
                  )}
                  replaceEmptyWithDash
                  value={userSelectedData.roles ?? []}
                  chipPopoverGridRef={gridRef}
                  chipPopoverHeaderLabel={getUserLabel(
                    createUserSiteCoreKeys.selectedRoles,
                  )}
                  isValueWithEllipsis
                  showMoreARIALabel={commonARIALabel.showNMore}
                  removeChipNameARIALabel={commonARIALabel.removeChipName}
                  closePopoverARIALabel={commonARIALabel.closePopover}
                />
              </MuiGrid>
            </MuiGrid>
          </MuiBox>
          <MuiBox className="accordion-container container-second">
            <MuiTypography
              variant="h2SemiBold"
              className="role-header-text"
              component="div"
            >
              {getUserLabel(createUserSiteCoreKeys.clientPortalSubTitle)}
            </MuiTypography>
            <MuiGrid container>
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabel
                  label={getUserLabel(createUserSiteCoreKeys.clientPortalRole)}
                  replaceEmptyWithDash
                  value={userData?.clientPortalAdminRole}
                  isValueWithEllipsis
                />
              </MuiGrid>
            </MuiGrid>
          </MuiBox>
        </AccordionHeader>
        <AccordionHeader
          className="accordion-header"
          title={renderHeader(
            getUserLabel(
              createUserSiteCoreKeys.roleDetailsLocationAccessSectionTitle,
            ),
          )}
          isExpanded={true}
          showExpandCollapseIcon={false}
        >
          <MuiGrid container className="accordion-container location-access">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabelWithChip
                label={getUserLabel(createUserSiteCoreKeys.country)}
                replaceEmptyWithDash
                value={userSelectedData.countries}
                chipPopoverGridRef={countryRef}
                chipPopoverHeaderLabel={getUserLabel(
                  createUserSiteCoreKeys.selectedCountries,
                )}
                isValueWithEllipsis
                showMoreARIALabel={commonARIALabel.showNMore}
                removeChipNameARIALabel={commonARIALabel.removeChipName}
                closePopoverARIALabel={commonARIALabel.closePopover}
              />
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabelWithChip
                label={getUserLabel(createUserSiteCoreKeys.serviceOffice)}
                replaceEmptyWithDash
                value={userSelectedData.serviceOffices}
                chipPopoverGridRef={gridRef}
                chipPopoverHeaderLabel={getUserLabel(
                  createUserSiteCoreKeys.selectedServiceOffices,
                )}
                isValueWithEllipsis
                showMoreARIALabel={commonARIALabel.showNMore}
                removeChipNameARIALabel={commonARIALabel.removeChipName}
                closePopoverARIALabel={commonARIALabel.closePopover}
                isDialogTitleWithEllipsis
              />
            </MuiGrid>
          </MuiGrid>
        </AccordionHeader>
        {userData?.hasAccessToPowerBIReport && (
          <AccordionHeader
            className="accordion-header"
            title={renderHeader(
              getUserLabel(
                createUserSiteCoreKeys.powerBIReportAccessSectionTitle,
              ),
            )}
            isExpanded={true}
            showExpandCollapseIcon={false}
          >
            <MuiGrid container className="accordion-container power-bi-container">
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabelWithChip
                  label={getUserLabel(
                    createUserSiteCoreKeys.selectedPreference,
                  )}
                  replaceEmptyWithDash
                  value={[
                    {
                      label: getUserLabel(
                        createUserSiteCoreKeys.provideAccessForPowerBIReportsCheckBoxText,
                      ),
                      value: "",
                    },
                  ]}
                  chipPopoverGridRef={gridRef}
                  isValueWithEllipsis
                  showMoreARIALabel=""
                  removeChipNameARIALabel=""
                  closePopoverARIALabel=""
                  isDialogTitleWithEllipsis
                />
              </MuiGrid>
            </MuiGrid>
          </AccordionHeader>
        )}
      </MuiBox>
    </CreateUserRoleDetailWrapper>
  );
};
